import { getCookieDomain } from "./cookieDomain";

/**
 * Clasificación por categoría de las cookies propias que escribe la app.
 *
 * `NECESSARY_MEASUREMENT_COOKIES` — identificadores de clic (gclid, gbraid,
 * wbraid, msclkid, fbclid, ucc), `utm_*`, `tid`, `entryPage`, `referrerUrl` y
 * `gtf_campaign`. Decisión de negocio (Carlos, 2026-09-15): son medición
 * anónima del origen del tráfico, no identifican a la persona ni se comparten
 * con terceros, y sin ellas no se puede facturar ni conciliar la adquisición,
 * así que se tratan como estrictamente necesarias: se escriben en el acto, no
 * se retienen ni se borran a la espera del banner, y el `POST
 * /api/lead/track/v2` sale siempre con la atribución resuelta. El
 * consentimiento sigue mandando sobre las etiquetas de terceros (Consent Mode
 * v2 en public/index.html, VWO) y sobre todo lo demás del RGPD (marketing,
 * datos de salud), que no se toca.
 *
 * `CONSENT_GATED_COOKIES` — analítica propia de identidad (visitor_id,
 * session_id, user_id): siguen esperando a que CookieYes acepte la categoría
 * "analytics". Hasta entonces viven en `sessionStorage` para no romper el
 * handoff web → app dentro de la misma sesión y se persisten en cuanto llega
 * el consentimiento (mismo mecanismo que el gate de VWO).
 *
 * Las cookies funcionales (token, themeColor, logo, brokerPhone…) no pasan por
 * ninguna de las dos listas y se escriben directas.
 */
export const NECESSARY_MEASUREMENT_COOKIES: ReadonlySet<string> = new Set([
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "ucc",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_test",
  "tid",
  "entryPage",
  "referrerUrl",
  "gtf_campaign",
]);

export const CONSENT_GATED_COOKIES: ReadonlySet<string> = new Set([
  "visitor_id",
  "session_id",
  "user_id",
]);

export const CONSENT_COOKIE = "cookieyes-consent";
export const CONSENT_UPDATE_EVENT = "cookieyes_consent_update";
const DEFERRED_STORE_KEY = "l5.deferred-cookies";
const EXPIRED = "Thu, 01 Jan 1970 00:00:00 GMT";

interface DeferredCookie {
  value: string | null;
  expires: string;
}

type DeferredStore = Record<string, DeferredCookie>;

export const isConsentGatedCookie = (name: string): boolean =>
  CONSENT_GATED_COOKIES.has(name);

/** Medición de atribución: categoría necesaria, nunca supeditada al banner. */
export const isNecessaryMeasurementCookie = (name: string): boolean =>
  NECESSARY_MEASUREMENT_COOKIES.has(name);

/**
 * Cookies que pudieron quedar retenidas en la cola diferida. Incluye las de
 * atribución porque una pestaña abierta antes de este cambio puede llevar sus
 * valores en `sessionStorage`; se siguen leyendo y se promocionan a cookie.
 */
const isDeferrableCookie = (name: string): boolean =>
  isConsentGatedCookie(name) || isNecessaryMeasurementCookie(name);

const readConsentCookie = (): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`)
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

/** Lee la decisión de CookieYes desde la cookie compartida con la web (life5.xx). */
export const analyticsConsentGranted = (): boolean =>
  analyticsConsentState() === "granted";

export type AnalyticsConsentState = "granted" | "denied" | "pending";

/**
 * Consentimiento de analítica según CookieYes: `granted` si aceptó
 * "analytics"; `denied` si ya decidió (`action:yes`) sin aceptarla; `pending`
 * si aún no ha tocado el banner (o no hay cookie).
 */
export const analyticsConsentState = (): AnalyticsConsentState => {
  const value = readConsentCookie();
  if (!value) return "pending";
  if (/(^|,)analytics:yes(,|$)/.test(value)) return "granted";
  if (/(^|,)action:yes(,|$)/.test(value)) return "denied";
  return "pending";
};

const readStore = (): DeferredStore => {
  try {
    const raw = window.sessionStorage.getItem(DEFERRED_STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as DeferredStore) : {};
  } catch {
    return {};
  }
};

const writeStore = (store: DeferredStore): void => {
  try {
    if (0 === Object.keys(store).length) {
      window.sessionStorage.removeItem(DEFERRED_STORE_KEY);
    } else {
      window.sessionStorage.setItem(DEFERRED_STORE_KEY, JSON.stringify(store));
    }
  } catch {
    // sin sessionStorage (modo privado, SSR) el valor solo vive en la cookie tras consentir
  }
};

const isExpired = (entry: DeferredCookie): boolean =>
  new Date(entry.expires).getTime() <= Date.now();

/**
 * Escritura real en `document.cookie` (dominio compartido .life5.xx, path=/,
 * SameSite=Lax, Secure en https). Única salida para todas las cookies propias.
 */
export const writeCookieRaw = (
  name: string,
  value: string | null,
  expires: Date
): void => {
  if (typeof document === "undefined") return;

  const encoded = value ? encodeURIComponent(value) : value;
  const domain = getCookieDomain();
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:";

  if (domain) {
    // Drop any host-only copy left by builds that ran without a cookie
    // domain, so it cannot shadow the shared cookie on the registrable domain.
    document.cookie = `${name}=;expires=${EXPIRED};path=/`;
  }

  const parts = [
    `${name}=${encoded}`,
    `expires=${expires.toUTCString()}`,
    "path=/",
    "SameSite=Lax",
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (secure) parts.push("Secure");

  document.cookie = parts.join(";");
};

/** Valor pendiente de consentimiento (en memoria de sesión), si existe y no ha caducado. */
export const readDeferredCookie = (name: string): string | null => {
  if (!isDeferrableCookie(name)) return null;
  const entry = readStore()[name];
  if (!entry || null === entry.value || isExpired(entry)) return null;
  return entry.value;
};

export const clearDeferredCookie = (name: string): void => {
  const store = readStore();
  if (!(name in store)) return;
  delete store[name];
  writeStore(store);
};

const deferCookie = (name: string, value: string | null, expires: Date): void => {
  const store = readStore();
  store[name] = { value, expires: expires.toISOString() };
  writeStore(store);
};

/** Persiste en cookie todo lo pendiente que siga vigente y vacía la cola. */
export const flushDeferredCookies = (): void => {
  const store = readStore();
  Object.entries(store).forEach(([name, entry]) => {
    if (isExpired(entry)) return;
    writeCookieRaw(name, entry.value, new Date(entry.expires));
  });
  writeStore({});
};

let necessaryPromoted = false;

/**
 * Saca de la cola diferida las cookies que ahora son necesarias (atribución) y
 * las escribe ya. Solo puede haber valores ahí en sesiones que empezaron con el
 * build anterior, en las que el consentimiento nunca llegó.
 */
export const promoteNecessaryDeferredCookies = (): void => {
  const store = readStore();
  let changed = false;
  Object.entries(store).forEach(([name, entry]) => {
    if (!isNecessaryMeasurementCookie(name)) return;
    if (!isExpired(entry)) writeCookieRaw(name, entry.value, new Date(entry.expires));
    delete store[name];
    changed = true;
  });
  if (changed) writeStore(store);
};

let listenerInstalled = false;

/** Escucha la decisión tomada en la app (la de la web llega ya en la cookie). */
export const installConsentListener = (): void => {
  if (listenerInstalled || typeof document === "undefined") return;
  listenerInstalled = true;
  document.addEventListener(CONSENT_UPDATE_EVENT, (event: Event) => {
    const accepted: unknown = (event as CustomEvent)?.detail?.accepted;
    if (Array.isArray(accepted) && accepted.includes("analytics")) {
      flushDeferredCookies();
    }
  });
};

/** Solo para tests. */
export const resetConsentListenerForTests = (): void => {
  listenerInstalled = false;
  necessaryPromoted = false;
};

/**
 * Punto único de escritura de cookies propias: la analítica de identidad se
 * difiere hasta el consentimiento; la medición de atribución (categoría
 * necesaria), las funcionales y los borrados van directas.
 */
export const setCookieWithConsent = (
  name: string,
  value: string | null,
  expires: Date
): void => {
  if (!necessaryPromoted && typeof window !== "undefined") {
    necessaryPromoted = true;
    promoteNecessaryDeferredCookies();
  }

  if (!isConsentGatedCookie(name)) {
    writeCookieRaw(name, value, expires);
    return;
  }

  const clearing = !value || expires.getTime() <= Date.now();
  if (clearing) {
    clearDeferredCookie(name);
    writeCookieRaw(name, value, expires);
    return;
  }

  if (analyticsConsentGranted()) {
    clearDeferredCookie(name);
    writeCookieRaw(name, value, expires);
    return;
  }

  installConsentListener();
  deferCookie(name, value, expires);
};
